import { useEffect, useState } from 'react';
import { Col, Container, Row } from 'reactstrap';
import Layout from '../../templates/Layout';
import { useCertificateId } from '../../../lib/util';
import {
  DUPLICATE_CERTIFICATE,
  GET_ALL_CERTIFICATES,
  GET_CERTIFICATE_DETAILS,
  UPDATE_CERTIFICATE,
} from '../../../graph-queries/tar/certificate';
import { useMutation, useQuery } from '@apollo/client';
import { TCertificate } from '../../../lib/types';
import classes from './CertificateDetails.module.scss';
import { Button } from '@bphxd/ds-core-react';
import {
  Attach16,
  Check16,
  CounterClockwise16,
  Download16,
  Duplicate16,
  Edit16,
  ExternalLink16,
  Lock16,
  Swap16,
  SwapUsers16,
} from '@bphxd/ds-core-react/lib/icons';
import { FormModal } from '../../molecules/FormModal/FormModal';
import { useHasMaintenanceAdminAccess, useHasMaintenanceUserAccess } from '../../../lib/util/auth';
import { extractAndFormatName } from '../../organisms/AspectsTable/util';
import CertificateForm from '../../organisms/CertificateForm/CertificateForm';
import CertificateJointsTable from '../../organisms/CertificateJointsTable/CertificateJointsTable';
import ConfirmationModal from '../../molecules/ConfirmationModal/ConfirmationModal';
import { useCertificateForExport } from '../Certificates/Certificates.util';
import { GET_EKG_FIELDS, GET_MO_DETAILS } from '../../../graph-queries/tar/job';
import { GET_CONFIG } from '../../../graph-queries/common/config';
import { useAizeSearchUrlByTagNumber } from '../JobDetails/JobDetails';
import { CertificateStatusEnum } from '../../../graph-queries/__generated__/graphql';
import { CERTIFICATE_STATUS_ENUM } from '../../../lib/constants/dbConstants';
import CertificateAttachments from '../../organisms/CertificateAttachments/CertificateAttachments';
import DuplicateCertificateForm from '../Certificates/DuplicateCertificateForm';
import { useNavigate } from 'react-router-dom';
import { MAINTANENCE_PATH } from '../../../constants';
import { useMsal } from '@azure/msal-react';

const withRequiredClassName = (
  IconComponent: React.FC<{ className?: string }>
): React.FC<{ className: string }> => {
  const WrappedIcon: React.FC<{ className: string }> = (props) => <IconComponent {...props} />;
  WrappedIcon.displayName = `WithRequiredClassName(${IconComponent.displayName || IconComponent.name || 'Icon'})`;
  return WrappedIcon;
};

const CertificateDetailsPage = () => {
  const hasUserAccess = useHasMaintenanceUserAccess();
  const hasMaintAdminAccess = useHasMaintenanceAdminAccess();
  const { accounts } = useMsal();
  const currentUser = accounts[0];
  const currentUserName = currentUser?.name;
  const currentUserEmail = currentUser?.username;
  const certificateId = useCertificateId();
  const exportCertificate = useCertificateForExport();
  const { data: config } = useQuery(GET_CONFIG);
  const { data, loading } = useQuery(GET_CERTIFICATE_DETAILS, { variables: { certificateId } });
  const certificateDetails = data?.certificateDetails as unknown as TCertificate;
  const [alertText, setAlertText] = useState('');
  const navigate = useNavigate();
  const isRealTimeEnabled = !!config?.config.featureFlags.find(
    (ff) => ff.flag === 'FEATURE_FLAG_REALTIME_MO_API'
  )?.isEnabled;

  const { data: moData, loading: moLoading } = useQuery(GET_MO_DETAILS, {
    variables: {
      moNumber: data?.certificateDetails?.maintenanceOrder || '',
      isRealTime: isRealTimeEnabled,
    },
    skip: !data?.certificateDetails?.maintenanceOrder,
  });
  const {
    data: ekgData,
    loading: ekgLoading,
    error: ekgError,
  } = useQuery(GET_EKG_FIELDS, {
    variables: { location: moData?.sapMo.location || '' },
    skip: !moData?.sapMo.location,
  });
  const [duplicateCertificate, { loading: isDuplicating }] = useMutation(DUPLICATE_CERTIFICATE, {
    refetchQueries: [GET_ALL_CERTIFICATES],
    awaitRefetchQueries: true,
  });

  const fullAizeUrl = useAizeSearchUrlByTagNumber(ekgData?.ekgFields.tagNumber || '');

  const [updateCertificate] = useMutation(UPDATE_CERTIFICATE, {
    refetchQueries: [
      {
        query: GET_CERTIFICATE_DETAILS,
        variables: { certificateId },
      },
    ],
  });

  const isLoading = loading || isDuplicating;

  useEffect(() => {
    if (alertText) {
      const timer = setTimeout(() => setAlertText(''), 2000);
      return () => clearTimeout(timer);
    }
  }, [alertText]);

  const renderCertificateForm = (onClose: () => void, certificate: TCertificate) => {
    return (
      <CertificateForm
        defaultValues={certificate}
        onSubmit={async (certificate: TCertificate) => {
          const { createdAt, updatedAt, status, ...input } = certificate;
          return updateCertificate({ variables: { input } }).then(() =>
            setAlertText('Certificate successfully updated')
          );
        }}
        onClose={onClose}
        type={'edit'}
      />
    );
  };

  const handleExportExcel = () => exportCertificate(certificateId);

  const statusButtonMapping: Record<
    Exclude<CertificateStatusEnum, CertificateStatusEnum.Complete>,
    {
      buttonText: string;
      nextStatus: CertificateStatusEnum;
      Icon: React.FC<{ className: string }>;
    }
  > = {
    [CertificateStatusEnum.Draft]: {
      buttonText: 'Issue for Review',
      nextStatus: CertificateStatusEnum.IssuedForReview,
      Icon: withRequiredClassName(SwapUsers16),
    },
    [CertificateStatusEnum.IssuedForReview]: {
      buttonText: 'Approve',
      nextStatus: CertificateStatusEnum.Approved,
      Icon: withRequiredClassName(Lock16),
    },
    [CertificateStatusEnum.Approved]: {
      buttonText: 'Unlock',
      nextStatus: CertificateStatusEnum.Draft,
      Icon: withRequiredClassName(CounterClockwise16),
    },
  };

  const completeMapping = {
    buttonText: 'Complete',
    Icon: withRequiredClassName(Check16),
  };

  const currentStatus = certificateDetails?.status as CertificateStatusEnum;
  const statusButtonProps = currentStatus
    ? currentStatus === CertificateStatusEnum.Complete
      ? completeMapping
      : statusButtonMapping[
          currentStatus as Exclude<CertificateStatusEnum, CertificateStatusEnum.Complete>
        ]
    : undefined;
  const nextStatus =
    currentStatus && currentStatus !== CertificateStatusEnum.Complete
      ? statusButtonMapping[
          currentStatus as Exclude<CertificateStatusEnum, CertificateStatusEnum.Complete>
        ].nextStatus
      : undefined;

  const handleStatusChange = () => {
    if (certificateDetails && nextStatus) {
      updateCertificate({
        variables: {
          input: {
            certificateId,
            assetId: certificateDetails?.assetId,
            title: certificateDetails?.title,
            type: certificateDetails?.type,
            certificateNumber: certificateDetails?.certificateNumber,
            status: nextStatus,
            template: certificateDetails?.template,
          },
        },
      }).then(() => setAlertText('Certificate status updated'));
    }
  };
  const onDuplicationSubmit = async (certificateId: TCertificate['certificateId']) => {
    return duplicateCertificate({ variables: { certificateId } }).then((res) => {
      navigate(
        `/${MAINTANENCE_PATH}/Certificates/${res.data?.duplicateCertificate.certificateId}${location.search}`
      );
      return res;
    });
  };

  const disableApproval =
    certificateDetails?.createdBy === `[${currentUserName}](${currentUserEmail})`;

  return (
    <Layout>
      <Container>
        {loading ? (
          'Loading...'
        ) : (
          <div className={classes.root}>
            <section className="border border-primary bg-white">
              <header className="p-4">
                <div className={classes.title} title={certificateDetails?.title}>
                  {certificateDetails.title} {certificateDetails.template ? '[TEMPLATE]' : ''}
                </div>
                <div>
                  {certificateDetails.template && (
                    <FormModal
                      title="Duplicate Certificate"
                      renderForm={(onClose) => (
                        <DuplicateCertificateForm
                          certificateId={certificateDetails.certificateId}
                          onSubmit={onDuplicationSubmit}
                          onClose={onClose}
                          isLoading={isLoading}
                        />
                      )}
                      renderButton={(onClick) => (
                        <Button
                          rounded="0"
                          size="sm"
                          level="tertiary"
                          Icon={Duplicate16}
                          onClick={onClick}
                          title="Duplicate Certificate"
                          className="me-2"
                          disabled={currentStatus !== CertificateStatusEnum.Approved}
                        >
                          Duplicate
                        </Button>
                      )}
                    />
                  )}
                  <FormModal
                    title="Certificate Attachments"
                    renderForm={(onClose) => (
                      <CertificateAttachments certificateId={certificateId} onClose={onClose} />
                    )}
                    renderButton={(onClick) => (
                      <Button
                        rounded="0"
                        size="sm"
                        level="tertiary"
                        Icon={Attach16}
                        onClick={onClick}
                        title="Add attachment"
                        className="me-2"
                      >
                        Add Attachment
                      </Button>
                    )}
                  />
                  <Button
                    rounded="0"
                    size="sm"
                    level="tertiary"
                    Icon={ExternalLink16}
                    onClick={() => window.open(fullAizeUrl, '_blank', 'noreferrer')}
                    title="Launch Digital Twin"
                    className="me-2"
                  >
                    Launch Twin
                  </Button>
                  <Button
                    rounded="0"
                    size="sm"
                    level="tertiary"
                    Icon={Download16}
                    onClick={handleExportExcel}
                    title="Export Excel"
                    className="me-2"
                  >
                    Export Excel
                  </Button>
                  <FormModal
                    title="Edit Certificate"
                    renderForm={(onClose) => renderCertificateForm(onClose, certificateDetails)}
                    renderButton={(onClick) => (
                      <Button
                        disabled={!hasUserAccess}
                        rounded="0"
                        size="sm"
                        level="tertiary"
                        Icon={Edit16}
                        onClick={onClick}
                        title="Edit Certificate"
                        className="me-2"
                      >
                        Edit Certificate Details
                      </Button>
                    )}
                  />
                  {statusButtonProps &&
                    (currentStatus === CertificateStatusEnum.Complete ? (
                      <Button
                        disabled
                        rounded="0"
                        size="sm"
                        level="tertiary"
                        className="me-2"
                        Icon={statusButtonProps.Icon}
                        title="Certificate is complete"
                      >
                        {statusButtonProps.buttonText}
                      </Button>
                    ) : (
                      <ConfirmationModal
                        title="Change Certificate Status"
                        message={`Are you sure you want to change status from ${CERTIFICATE_STATUS_ENUM[currentStatus]} to ${CERTIFICATE_STATUS_ENUM[nextStatus as CertificateStatusEnum]}?`}
                        confirmButtonText="Yes, change status"
                        cancelButtonText="Cancel"
                        onConfirm={handleStatusChange}
                        renderButton={(openModal) => (
                          <Button
                            disabled={
                              currentStatus === CertificateStatusEnum.Approved
                                ? !hasMaintAdminAccess
                                : currentStatus === CertificateStatusEnum.IssuedForReview
                                  ? !hasUserAccess || disableApproval
                                  : !hasUserAccess
                            }
                            rounded="0"
                            size="sm"
                            level="tertiary"
                            className="me-2"
                            Icon={statusButtonProps.Icon}
                            onClick={openModal}
                            title={
                              currentStatus === CertificateStatusEnum.Approved
                                ? !hasMaintAdminAccess
                                  ? "You don't have sufficient privileges"
                                  : `Set status to ${CERTIFICATE_STATUS_ENUM[nextStatus as CertificateStatusEnum]}`
                                : !hasUserAccess
                                  ? "You don't have sufficient privileges"
                                  : `Set status to ${CERTIFICATE_STATUS_ENUM[nextStatus as CertificateStatusEnum]}`
                            }
                          >
                            {statusButtonProps.buttonText}
                          </Button>
                        )}
                      />
                    ))}
                  {currentStatus === CertificateStatusEnum.Approved &&
                    certificateDetails.template === false && (
                      <Button
                        rounded="0"
                        size="sm"
                        level="tertiary"
                        Icon={Swap16}
                        className="me-2"
                        onClick={() =>
                          navigate(
                            `/${MAINTANENCE_PATH}/Certificates/execute/${data?.certificateDetails.certificateId}${location.search}`
                          )
                        }
                        title={'Switch to execute view'}
                      >
                        {'Switch View'}
                      </Button>
                    )}
                </div>
              </header>
              <div className="p-4">
                <Row className="g-12">
                  <Col>
                    <div className={classes.field}>
                      <div className={classes.label}>Certificate Number</div>
                      <div className={classes.value}>
                        {certificateDetails?.certificateNumber || certificateDetails?.certificateId}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Maintenance Order</div>
                      <div className={classes.value}>
                        <a href={moData?.sapMo.viewInFiori || ''} target="_blank" rel="noreferrer">
                          {certificateDetails?.maintenanceOrder}
                        </a>
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Location</div>
                      <div className={classes.value}>{certificateDetails?.location}</div>
                    </div>
                  </Col>
                  <Col>
                    <div className={classes.field}>
                      <div className={classes.label}>System</div>
                      <div className={classes.value}>{certificateDetails?.system}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Lubricant Used</div>
                      <div className={classes.value}>{certificateDetails?.lubricantUsed}</div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Permit Number</div>
                      <div className={classes.value}>
                        <a
                          href={
                            `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${certificateDetails?.permitNumber?.replace('#', '%23')}?openerRegistrationNumber=Permit` ||
                            ''
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          {certificateDetails?.permitNumber}
                        </a>
                      </div>
                    </div>
                  </Col>
                  <Col>
                    <div className={classes.field}>
                      <div className={classes.label}>Isolation Number</div>
                      <div className={classes.value}>
                        <a
                          href={
                            `https://ecow.bpglobal.com/permitvision/permitvision/editor/certificate/${certificateDetails?.isolationNumber?.replace('#', '%23')}?openerRegistrationNumber=Isolation` ||
                            ''
                          }
                          target="_blank"
                          rel="noreferrer"
                        >
                          {certificateDetails?.isolationNumber}
                        </a>
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Created By</div>
                      <div className={classes.value}>
                        {certificateDetails?.createdBy
                          ? extractAndFormatName(certificateDetails?.createdBy)
                          : ''}
                      </div>
                    </div>
                    <div className={classes.field}>
                      <div className={classes.label}>Checked By</div>
                      <div className={classes.value}>
                        {extractAndFormatName(certificateDetails?.checkedBy)}
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            </section>
            <section className="border border-primary bg-white">
              <CertificateJointsTable certificateId={certificateDetails?.certificateId} />
            </section>
          </div>
        )}
      </Container>
    </Layout>
  );
};

CertificateDetailsPage.classes = classes;

export default CertificateDetailsPage;
